'use client';

import React from 'react';
import { useRelatedProducts } from '@eliadmin/core/src/hooks/products/use-related-products';

import type { ProductDetailData } from '@eliadmin/core/src/api/products/types/product-types';

import { DescriptionAndParameters } from './tabs/description-and-parameters';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import {
  fetchProductQuestions,
  fetchProductReviews,
} from '@eliadmin/core/src/api/products/product-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { ProductParameters } from './tabs/product-parameters';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';

// Only the selected tab's content is mounted below. Keep the initial description in SSR,
// but do not download forms and variant tables for tabs that have not been opened.
const ProductReviews = React.lazy(() =>
  import('./tabs/product-reviews').then(m => ({ default: m.ProductReviews })),
);
const ProductQuestions = React.lazy(() =>
  import('./tabs/product-questions').then(m => ({ default: m.ProductQuestions })),
);
const ProductVariants = React.lazy(() =>
  import('./tabs/product-variants').then(m => ({ default: m.ProductVariants })),
);
const ProductVideo = React.lazy(() =>
  import('./tabs/product-video').then(m => ({ default: m.ProductVideo })),
);
const ProductAttachments = React.lazy(() =>
  import('./tabs/product-attachments').then(m => ({ default: m.ProductAttachments })),
);

export function ProductContent({ product }: { product: ProductDetailData }) {
  const related = useRelatedProducts(product.slug);
  const locale = useLocaleStore(s => s.currentLocale);
  const selectedVariant = useVariantsStore(state => state.selectedVariant);
  const [activeTab, setActiveTab] = React.useState('description');

  const { data: reviews, isLoading } = useQuery({
    queryKey: [CacheKey.PRODUCTS_REVIEWS, locale, product.slug],
    queryFn: () => fetchProductReviews({ locale, slug: product.slug }),
    enabled: activeTab === 'reviews',
  });

  const { data: questions = { data: [] } } = useQuery({
    queryKey: ['QUESTIONS', locale, product.id],
    queryFn: () => fetchProductQuestions({ locale, id: product.id }),
    enabled: activeTab === 'questions',
  });

  const tabs = [
    {
      id: 'description',
      name: 'Popis a parametre',
      content: <DescriptionAndParameters product={product} />,
      show: product.description || (product?.attributes && product.attributes.length > 0),
      number: null,
    },
    {
      id: 'parameters',
      name: 'Parametre',
      content: <ProductParameters product={product} />,
      show: product?.attributes && product.attributes.length > 0,
      number: selectedVariant?.values?.length || product?.attributes?.length,
    },
    {
      id: 'video',
      name: 'Video',
      content: <ProductVideo product={product} />,
      show: product?.video,
      number: null,
    },
    {
      id: 'variants',
      name: 'Varianty',
      content: <ProductVariants product={product} />,
      show: product?.variants && product.variants.length > 0,
      number: product?.variants?.length,
    },
    {
      id: 'attachments',
      name: 'Na stiahnutie',
      content: <ProductAttachments product={product} />,
      show: product?.attachments && product.attachments.length > 0,
      number: product?.attachments?.length,
    },
    {
      id: 'reviews',
      name: 'Recenzie',
      content: (
        <ProductReviews product={product} reviews={reviews?.data ?? []} isLoading={isLoading} />
      ),
      show: true,
      number: reviews?.data.length ?? product.ratingCount ?? product.reviews_count ?? 0,
    },
    {
      id: 'questions',
      name: 'Otázky',
      content: <ProductQuestions productId={product.id} questions={questions.data} />,
      show: true,
      number: questions?.data.length || 0,
    },
  ];

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  return (
    <section>
      <Tabs
        onValueChange={value => handleTabChange(value)}
        value={activeTab}
        defaultValue={activeTab}
        className={cn('flex w-full flex-col items-center justify-center gap-8', {
          'border-b border-b-border pb-12': related.products.length > 0,
        })}
      >
        <TabsList
          className={cn(
            'custom-section',
            'h-fit overflow-x-scroll rounded-none border-b border-b-border bg-transparent py-0',
            'hide-scroll-bar',
          )}
        >
          <div className={cn('custom-container', 'flex items-center justify-start gap-0')}>
            {tabs.map(tab => {
              if (tab.show) {
                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className={cn(
                      'flex items-center justify-center gap-2 rounded-none border-b-2 px-4 py-[18px] text-sm font-normal transition-all sm:text-base lg:text-lg',
                      'ea-product-content__tab-trigger',
                    )}
                  >
                    {tab.name}
                    {(tab.number ?? 0) > 0 && (
                      <p className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-center text-[10px] font-medium text-white">
                        {tab.number}
                      </p>
                    )}
                  </TabsTrigger>
                );
              }
            })}
          </div>
        </TabsList>
        {tabs.filter(tab => tab.show || tab.id === activeTab).map(tab => (
          <TabsContent
            key={tab.id}
            value={tab.id}
            forceMount
            hidden={activeTab !== tab.id}
            style={activeTab === tab.id ? undefined : { display: 'none' }}
            className={cn('custom-section', 'w-full', tab.id === 'variants' && 'px-0')}
          >
            {activeTab === tab.id && (
              <div className={cn('custom-container')}>
                <React.Suspense
                  fallback={
                    <div className="min-h-32" role="status" aria-busy="true">
                      Načítava sa…
                    </div>
                  }
                >
                  {tab.content}
                </React.Suspense>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
}
